import { z } from "zod";

export const NIGERIAN_MOBILE_NUMBER_PATTERN = /^(?:\+?234|0)[789][01]\d{8}$/;
export const nigerianPhoneNumberSchema = z
  .string()
  .trim()
  .regex(
    NIGERIAN_MOBILE_NUMBER_PATTERN,
    "Enter a valid Nigerian number (e.g. 08032000102 or +2348032000102)",
  );

export const customerStatusSchema = z.enum(["UNVERIFIED", "ACTIVE", "SUSPENDED"]);
export const userRoleSchema = z.enum(["OWNER", "SUPER_ADMIN", "CUSTOMER", "ADMIN", "RIDER"]);

export const apiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    message: z.string().min(1),
    status: z.int().min(100).max(599),
  });

export const apiErrorDataSchema = z.object({
  errors: z.array(z.string()),
  path: z.string(),
  requestId: z.string().nullable(),
  timestamp: z.iso.datetime(),
});

export const apiErrorResponseSchema = apiResponseSchema(apiErrorDataSchema);

export const registerCustomerInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    phone: nigerianPhoneNumberSchema,
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
    password: z.string().min(8).max(128),
  })
  .strict();

export const registrationResultSchema = z.object({
  customerId: z.uuid(),
  status: z.literal("UNVERIFIED"),
  otpExpiresInSeconds: z.int().positive(),
  verificationChannels: z.object({
    email: z.boolean(),
    phone: z.boolean(),
  }),
});

export const verificationChannelSchema = z.enum(["phone", "email"]);

export const verifyUserInputSchema = z.discriminatedUnion("channel", [
  z
    .object({
      channel: z.literal("phone"),
      phone: nigerianPhoneNumberSchema,
      code: z.string().regex(/^\d{6}$/),
    })
    .strict(),
  z
    .object({
      channel: z.literal("email"),
      email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
      code: z.string().regex(/^\d{6}$/),
    })
    .strict(),
]);

export const userVerificationResultSchema = z.object({
  customerId: z.uuid(),
  status: z.literal("ACTIVE"),
  channel: verificationChannelSchema,
  verifiedAt: z.iso.datetime(),
  verificationChannels: z.object({
    email: z.boolean(),
    phone: z.boolean(),
  }),
});

export const resendVerificationInputSchema = z.object({
  channel: verificationChannelSchema,
  phone: nigerianPhoneNumberSchema.optional(),
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)).optional(),
});

export const resendVerificationResultSchema = z.object({
  sent: z.boolean(),
  channel: verificationChannelSchema,
  otpExpiresInSeconds: z.int().positive(),
});

export const resetPasswordInputSchema = z
  .object({
    identifier: z.string().min(1),
    phoneCode: z.string().optional(),
    emailCode: z.string().optional(),
    newPassword: z.string().min(8).max(128),
  })
  .strict();

export const resetPasswordResultSchema = z.object({
  passwordChanged: z.boolean(),
});

export const loginInputSchema = z
  .object({
    identifier: z.string().trim().toLowerCase().min(1),
    password: z.string().min(8).max(128),
  })
  .strict();

export const forgotPasswordInputSchema = z.object({ identifier: z.string().min(1) }).strict();

export const forgotPasswordResultSchema = z.object({
  sent: z.boolean(),
  otpExpiresInSeconds: z.int().positive(),
});

export const loginResultSchema = z.object({
  user: z.object({
    id: z.uuid(),
    role: userRoleSchema,
    outletId: z.uuid().nullable(),
  }),
  accessTokenExpiresInSeconds: z.int().positive(),
  refreshTokenExpiresInSeconds: z.int().positive(),
});

export const refreshSessionResultSchema = loginResultSchema;

export const logoutResultSchema = z.object({
  loggedOut: z.literal(true),
});

export const createAdminInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
    phone: nigerianPhoneNumberSchema,
    outletId: z.uuid(),
  })
  .strict();

export const adminResultSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  role: z.literal("ADMIN"),
  outletId: z.uuid(),
  temporaryPassword: z.string().min(8),
});

export const auditLogSchema = z.object({
  id: z.uuid(),
  actorId: z.uuid().nullable(),
  actorRole: userRoleSchema.nullable(),
  action: z.string().min(1),
  method: z.string().min(1),
  path: z.string().min(1),
  statusCode: z.int().min(100).max(599),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  requestId: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.iso.datetime(),
});

export const paginatedAuditLogsSchema = z.object({
  auditLogs: z.array(auditLogSchema),
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
  next: z.int().nonnegative().nullable(),
  previous: z.int().nonnegative().nullable(),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export const auditLogQuerySchema = z
  .object({
    page: z.int().positive().optional(),
    limit: z.int().positive().max(100).optional(),
    actorId: z.uuid().optional(),
    action: z.string().min(1).optional(),
    resourceType: z.string().min(1).optional(),
    resourceId: z.string().min(1).optional(),
    dateFrom: z.iso.datetime().optional(),
    dateTo: z.iso.datetime().optional(),
  })
  .strict();

export const databaseBackupStatusSchema = z.enum(["NEVER_RUN", "RUNNING", "SUCCESS", "FAILED"]);

export const databaseBackupSettingsSchema = z.object({
  id: z.uuid(),
  isEnabled: z.boolean(),
  intervalMinutes: z.int().min(15).max(10080),
  recipientEmails: z.array(z.email()),
  recipientEmail: z.email().nullable(),
  lastRunAt: z.iso.datetime().nullable(),
  nextRunAt: z.iso.datetime().nullable(),
  lastStatus: databaseBackupStatusSchema,
  lastError: z.string().nullable(),
  lastFileName: z.string().nullable(),
  lastFileSizeBytes: z.int().nonnegative().nullable(),
  updatedById: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const updateDatabaseBackupSettingsInputSchema = z
  .object({
    isEnabled: z.boolean().optional(),
    intervalMinutes: z.int().min(15).max(10080).optional(),
    recipientEmails: z
      .array(z.string().trim().toLowerCase().pipe(z.email().max(254)))
      .max(20)
      .optional(),
    recipientEmail: z.string().trim().toLowerCase().pipe(z.email().max(254)).optional(),
  })
  .strict();

export const databaseBackupRunResultSchema = z.object({
  sent: z.literal(true),
  fileName: z.string().min(1),
  fileSizeBytes: z.int().nonnegative(),
  recipientEmails: z.array(z.email()),
  recipientEmail: z.email(),
  completedAt: z.iso.datetime(),
});

export const createRiderInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
    phone: nigerianPhoneNumberSchema,
    vehicleType: z.string().trim().min(2).max(40).optional(),
    plateNumber: z.string().trim().min(2).max(40).optional(),
  })
  .strict();

export const riderResultSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  role: z.literal("RIDER"),
  outletId: z.uuid().nullable(),
  vehicleType: z.string().nullable(),
  plateNumber: z.string().nullable(),
  riderStatus: z.string().nullable(),
  temporaryPassword: z.string().min(8),
  temporaryPasswordEmailSent: z.boolean().default(true),
  temporaryPasswordEmailError: z.string().nullable().default(null),
});

export const riderAdminSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1),
  role: z.literal("RIDER"),
  outletId: z.uuid().nullable(),
  vehicleType: z.string().nullable(),
  plateNumber: z.string().nullable(),
  riderStatus: z.string().nullable(),
  status: customerStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const updateRiderInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)).optional(),
    phone: nigerianPhoneNumberSchema.optional(),
    vehicleType: z.string().trim().min(2).max(40).nullable().optional(),
    plateNumber: z.string().trim().min(2).max(40).nullable().optional(),
    riderStatus: z.string().trim().min(2).max(40).nullable().optional(),
    status: customerStatusSchema.optional(),
  })
  .strict();

export const outletAdminSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  role: z.literal("ADMIN"),
  outletId: z.uuid(),
  email: z.email(),
  phone: z.string().min(1),
  status: customerStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const platformUserSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  email: z.email(),
  phone: z.string().min(1),
  status: customerStatusSchema,
  role: z.literal("CUSTOMER"),
  avatarUrl: z.url().nullable(),
  emailVerifiedAt: z.iso.datetime().nullable(),
  phoneVerifiedAt: z.iso.datetime().nullable(),
  hasDeviceToken: z.boolean(),
  orderCount: z.int().nonnegative(),
  totalSpendMinor: z.int().nonnegative(),
  lastOrderAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const platformUserListSchema = z.object({
  users: z.array(platformUserSchema),
  total: z.int().nonnegative(),
  limit: z.int().min(1).max(100),
  offset: z.int().nonnegative(),
  next: z.int().positive().nullable(),
  previous: z.int().positive().nullable(),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export const platformUserListQuerySchema = z
  .object({
    page: z.int().positive().optional(),
    limit: z.int().min(1).max(100).optional(),
    status: customerStatusSchema.optional(),
    q: z.string().trim().min(1).optional(),
  })
  .strict();

export const resendVerificationCodeInputSchema = z.discriminatedUnion("channel", [
  z
    .object({
      channel: z.literal("phone"),
      phone: nigerianPhoneNumberSchema,
    })
    .strict(),
  z
    .object({
      channel: z.literal("email"),
      email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
    })
    .strict(),
]);

export const resendVerificationCodeResultSchema = z.object({
  sent: z.literal(true),
  channel: verificationChannelSchema,
  otpExpiresInSeconds: z.int().positive(),
});

export const registrationResponseSchema = apiResponseSchema(registrationResultSchema);
export const userVerificationResponseSchema = apiResponseSchema(userVerificationResultSchema);
export const resendVerificationCodeResponseSchema = apiResponseSchema(
  resendVerificationCodeResultSchema,
);
export const loginResponseSchema = apiResponseSchema(loginResultSchema);
export const logoutResponseSchema = apiResponseSchema(logoutResultSchema);
export const adminResponseSchema = apiResponseSchema(adminResultSchema);

export const currencySchema = z.literal("NGN");

export const moneySchema = z.object({
  amountMinor: z.int().nonnegative(),
  currency: currencySchema,
});

// outletSummarySchema is defined after menuItem / modifier schemas (below) to avoid TDZ

export const userProfileSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  role: z.string(),
  outletId: z.uuid().nullable(),
  avatarUrl: z.url().nullable(),
  email: z.string(),
  phone: z.string(),
  verificationChannels: z.object({ email: z.boolean(), phone: z.boolean() }),
  pendingVerificationChannels: z.object({ email: z.boolean(), phone: z.boolean() }),
});

export const updateProfileInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
    phone: nigerianPhoneNumberSchema,
  })
  .strict();

export const profileUpdateResultSchema = userProfileSchema.extend({
  otpExpiresInSeconds: z.int().positive().nullable(),
});

export const verifyProfileChangeInputSchema = z
  .object({
    code: z.string().regex(/^\d{6}$/),
  })
  .strict();

export const createDeliveryAddressInputSchema = z
  .object({
    label: z.string().min(1).max(50),
    addressLine: z.string().min(3),
    city: z.string().min(1),
    state: z.string().min(1),
    latitude: z.number(),
    longitude: z.number(),
    isDefault: z.boolean(),
  })
  .strict();

export const deliveryAddressSummarySchema = z.object({
  id: z.uuid(),
  label: z.string(),
  addressLine: z.string(),
  city: z.string(),
  state: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  isDefault: z.boolean(),
});

export const validateAddressInputSchema = z
  .object({
    latitude: z.number(),
    longitude: z.number(),
  })
  .strict();

export const validateAddressResultSchema = z.object({
  deliverable: z.boolean(),
  zone: z
    .object({
      id: z.uuid(),
      name: z.string().min(1),
    })
    .nullable(),
});

export const deliveryAddressProviderSchema = z.enum(["google", "opencage"]);

export const deliveryAddressSuggestionSchema = z.object({
  id: z.string().min(1),
  description: z.string().min(1),
  provider: deliveryAddressProviderSchema,
  sessionToken: z.string().nullable(),
});

export const resolveDeliveryAddressInputSchema = z
  .object({
    input: z.string().trim().min(3).max(200).optional(),
    suggestionId: z.string().min(1).max(500).optional(),
    provider: deliveryAddressProviderSchema.optional(),
    sessionToken: z.string().max(120).optional(),
  })
  .strict();

export const resolvedDeliveryAddressSchema = z.object({
  addressLine: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  label: z.string().min(1),
  displayName: z.string().min(1),
  latitude: z.number(),
  longitude: z.number(),
  provider: deliveryAddressProviderSchema,
});

export const geofenceZoneSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  polygon: z.unknown(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const geofencePolygonCoordinatesSchema = z.array(z.array(z.tuple([z.number(), z.number()])));

export const createGeofenceZoneInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    coordinates: geofencePolygonCoordinatesSchema,
    isActive: z.boolean().optional(),
  })
  .strict();

export const updateGeofenceZoneInputSchema = createGeofenceZoneInputSchema.partial().strict();

export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8).max(128),
  })
  .strict();

export const changePasswordResultSchema = z.object({
  passwordChanged: z.boolean(),
});

export const initiatePaymentInputSchema = z
  .object({
    items: z
      .array(
        z.object({
          menuItemId: z.uuid(),
          quantity: z.int().positive(),
          modifiers: z.array(z.object({ modifierId: z.uuid() })),
          customerNote: z.string().optional(),
        }),
      )
      .min(1),
    deliveryMode: z.enum(["DELIVERY", "TAKEOUT"]),
    deliveryAddress: z.string().optional(),
    landmark: z.string().trim().max(255).optional(),
    deliveryLatitude: z.number().optional(),
    deliveryLongitude: z.number().optional(),
    recipientPhone: nigerianPhoneNumberSchema.optional(),
    preparationNote: z.string().max(1000).optional(),
    promoCode: z.string().trim().min(2).max(80).optional(),
    subtotalMinor: z.int().nonnegative(),
    deliveryFeeMinor: z.int().nonnegative(),
    serviceFeeMinor: z.int().nonnegative(),
    vatMinor: z.int().nonnegative(),
    discountMinor: z.int().nonnegative().optional(),
    platformCommissionMinor: z.int().nonnegative(),
    totalMinor: z.int().nonnegative(),
    returnUrl: z.string().trim().min(1).max(2_000).optional(),
    idempotencyKey: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export const retryPaymentInputSchema = z
  .object({
    returnUrl: z.string().trim().min(1).max(2_000).optional(),
  })
  .strict();

export const initiatePaymentResultSchema = z.object({
  masterOrderId: z.uuid(),
  paymentId: z.uuid(),
  reference: z.string(),
  checkoutUrl: z.string().nullable(),
  status: z.string(),
  totals: z.object({
    subtotalMinor: z.int().nonnegative(),
    deliveryFeeMinor: z.int().nonnegative(),
    serviceFeeMinor: z.int().nonnegative(),
    vatMinor: z.int().nonnegative(),
    discountMinor: z.int().nonnegative(),
    platformCommissionMinor: z.int().nonnegative(),
    totalMinor: z.int().nonnegative(),
    currency: currencySchema,
  }),
  splitBreakdown: z.array(
    z.object({
      outletId: z.uuid(),
      subaccountCode: z.string().nullable(),
      grossMinor: z.int().nonnegative(),
      commissionMinor: z.int().nonnegative(),
      netMinor: z.int().nonnegative(),
    }),
  ),
});

export const paymentVerifyResultSchema = z.object({
  status: z.enum(["PENDING", "SUCCESS", "FAILED"]),
  orderStatus: z.string().min(1),
});

export const processRefundInputSchema = z
  .object({
    amountMinor: z.int().positive().optional(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .strict();

export const requestRefundInputSchema = processRefundInputSchema.extend({
  subOrderId: z.uuid().optional(),
});

export const paymentRefundSchema = z.object({
  id: z.uuid(),
  paymentId: z.uuid(),
  reference: z.string().min(1),
  amountMinor: z.int().positive(),
  currency: currencySchema,
  status: z.enum(["PENDING", "SUCCESS", "FAILED"]),
  reason: z.string().nullable(),
  provider: z.string().min(1),
  providerRefundId: z.string().nullable(),
  requestedBy: z.uuid(),
  createdAt: z.iso.datetime(),
});

export const settlementStatusSchema = z.enum(["NO_ACTIVITY", "PENDING", "APPROVED"]);

export const outletSettlementSummarySchema = z.object({
  outletId: z.uuid(),
  outletName: z.string().min(1),
  imageUrl: z.string().nullable(),
  subaccountCode: z.string().nullable(),
  settlementDateFrom: z.iso.date(),
  settlementDateTo: z.iso.date(),
  completedSubOrders: z.int().nonnegative(),
  pendingSubOrders: z.int().nonnegative(),
  fulfillmentPendingSubOrders: z.int().nonnegative(),
  grossMinor: z.int().nonnegative(),
  dineoutGrossMinor: z.int().nonnegative(),
  totalCollectedMinor: z.int().nonnegative(),
  commissionMinor: z.int().nonnegative(),
  netMinor: z.int().nonnegative(),
  currency: currencySchema,
  status: settlementStatusSchema,
  approvalAvailable: z.boolean(),
  approvalUnavailableReason: z.string().nullable(),
  latestApprovedAt: z.iso.datetime().nullable(),
});

export const outletSettlementSummaryListSchema = z.array(outletSettlementSummarySchema);

export const outletSettlementExportSchema = z.object({
  filename: z.string().min(1),
  contentType: z.string().min(1),
  content: z.string(),
});

export const menuCategorySchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  name: z.string().min(1),
  sortOrder: z.int().nonnegative(),
  isActive: z.boolean(),
});

export interface MenuItemPricingFields {
  priceMinor: number;
  discountPriceMinor?: number | null;
  discountStartsAt?: string | Date | null;
  discountEndsAt?: string | Date | null;
}

export function isMenuItemDiscountActive(
  item: MenuItemPricingFields,
  at: Date = new Date(),
): boolean {
  if (
    item.discountPriceMinor === null ||
    item.discountPriceMinor === undefined ||
    item.discountPriceMinor <= 0 ||
    item.discountPriceMinor >= item.priceMinor
  ) {
    return false;
  }

  const atTime = at.getTime();
  const startsAt = item.discountStartsAt
    ? new Date(item.discountStartsAt).getTime()
    : Number.NEGATIVE_INFINITY;
  const endsAt = item.discountEndsAt
    ? new Date(item.discountEndsAt).getTime()
    : Number.POSITIVE_INFINITY;

  return atTime >= startsAt && atTime <= endsAt;
}

export function getMenuItemCurrentPriceMinor(
  item: MenuItemPricingFields,
  at: Date = new Date(),
): number {
  return isMenuItemDiscountActive(item, at) ? item.discountPriceMinor! : item.priceMinor;
}

export const menuItemSchema = z
  .object({
    id: z.uuid(),
    outletId: z.uuid(),
    categoryId: z.uuid(),
    name: z.string().min(1),
    description: z.string().nullable(),
    imageUrl: z.url().nullable(),
    deliveryTimeRange: z.string().nullable().optional(),
    ratingAverage: z.coerce.number().min(0).max(5).default(0),
    ratingCount: z.int().nonnegative().default(0),
    priceMinor: z.int().nonnegative(),
    discountPriceMinor: z.int().positive().nullable().default(null),
    discountStartsAt: z.iso.datetime().nullable().default(null),
    discountEndsAt: z.iso.datetime().nullable().default(null),
    currentPriceMinor: z.int().nonnegative().optional(),
    isDiscountActive: z.boolean().default(false),
    currency: currencySchema,
    isAvailable: z.boolean(),
    sortOrder: z.int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullable(),
  })
  .transform((item) => {
    const active = item.isDiscountActive || isMenuItemDiscountActive(item);
    const currentPriceMinor = active
      ? (item.discountPriceMinor ?? item.currentPriceMinor ?? item.priceMinor)
      : (item.currentPriceMinor ?? item.priceMinor);

    return {
      ...item,
      isDiscountActive: active,
      currentPriceMinor,
    };
  });

export const menuItemsPageSchema = z.object({
  items: z.array(menuItemSchema),
  total: z.int().nonnegative(),
  limit: z.int().min(1).max(100),
  offset: z.int().min(0),
  hasMore: z.boolean(),
});

export const updateMenuItemAvailabilityInputSchema = z
  .object({
    isAvailable: z.boolean(),
  })
  .strict();

export const rateMenuItemInputSchema = z
  .object({
    rating: z.int().min(1).max(5),
    comment: z.string().trim().max(1_000).optional(),
  })
  .strict();

export const itemModifierGroupSchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  name: z.string().min(1),
  minSelections: z.int().nonnegative(),
  maxSelections: z.int().positive(),
  isRequired: z.boolean(),
  sortOrder: z.int().nonnegative(),
});

export const itemModifierSchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  groupId: z.uuid(),
  name: z.string().min(1),
  priceDeltaMinor: z.int().nonnegative(),
  currency: currencySchema,
  isAvailable: z.boolean(),
  sortOrder: z.int().nonnegative(),
});

export const menuItemModifierGroupSchema = z.object({
  id: z.uuid(),
  menuItemId: z.uuid(),
  groupId: z.uuid(),
  sortOrder: z.int().nonnegative(),
});

export const deliveryPricingModelSchema = z.enum(["FLAT", "PER_KM", "PER_LOCATION"]);
export type DeliveryPricingModel = z.infer<typeof deliveryPricingModelSchema>;

export const deliveryLocationFeeSchema = z.object({
  locationName: z.string().min(1).max(120),
  zoneId: z.string().uuid().nullable().optional(),
  feeMinor: z.number().int().nonnegative(),
});
export type DeliveryLocationFee = z.infer<typeof deliveryLocationFeeSchema>;

export function calculateDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export interface CalculateOutletDeliveryFeeParams {
  pricingModel?: DeliveryPricingModel | string | null | undefined;
  flatFeeMinor?: number | null | undefined;
  baseFeeMinor?: number | null | undefined;
  pricePerKmMinor?: number | null | undefined;
  locationFees?: DeliveryLocationFee[] | null | undefined;
  outletLatitude?: number | null | undefined;
  outletLongitude?: number | null | undefined;
  deliveryLatitude?: number | null | undefined;
  deliveryLongitude?: number | null | undefined;
  zoneId?: string | null | undefined;
  zoneName?: string | null | undefined;
  fallbackFeeMinor?: number | undefined;
}

export function calculateOutletDeliveryFee(params: CalculateOutletDeliveryFeeParams): number {
  const model = params.pricingModel ?? "FLAT";
  const fallback = params.fallbackFeeMinor ?? 150_000;

  if (model === "PER_KM") {
    const baseFee = params.baseFeeMinor ?? 0;
    const pricePerKm = params.pricePerKmMinor ?? 0;
    if (
      params.outletLatitude != null &&
      params.outletLongitude != null &&
      params.deliveryLatitude != null &&
      params.deliveryLongitude != null
    ) {
      const distanceKm = calculateDistanceKm(
        params.outletLatitude,
        params.outletLongitude,
        params.deliveryLatitude,
        params.deliveryLongitude,
      );
      return Math.max(0, baseFee + Math.round(distanceKm * pricePerKm));
    }
    return Math.max(0, baseFee || (params.flatFeeMinor ?? fallback));
  }

  if (model === "PER_LOCATION") {
    const baseFallback = params.baseFeeMinor ?? params.flatFeeMinor ?? fallback;
    const locationFees = params.locationFees ?? [];
    if (locationFees.length > 0) {
      if (params.zoneId) {
        const matchByZone = locationFees.find(
          (loc) => loc.zoneId && loc.zoneId.toLowerCase() === params.zoneId?.toLowerCase(),
        );
        if (matchByZone) return matchByZone.feeMinor;
      }
      if (params.zoneName) {
        const target = params.zoneName.trim().toLowerCase();
        const matchByName = locationFees.find(
          (loc) =>
            loc.locationName.trim().toLowerCase() === target ||
            target.includes(loc.locationName.trim().toLowerCase()) ||
            loc.locationName.trim().toLowerCase().includes(target),
        );
        if (matchByName) return matchByName.feeMinor;
      }
    }
    return baseFallback;
  }

  return params.flatFeeMinor ?? fallback;
}

export const outletSummarySchema = z
  .object({
    id: z.uuid(),
    name: z.string().min(1),
    cuisineType: z.string().min(1),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    logoUrl: z.string().nullable().default(null),
    bannerUrl: z.string().nullable().default(null),
    isOnline: z.boolean(),
    settlementSubaccountCode: z.string().nullable().optional(),
    ratingAverage: z.coerce.number().min(0).max(5).default(0),
    ratingCount: z.int().nonnegative().default(0),
    vatBps: z.int().min(0).max(10_000).default(0),
    deliveryPricingModel: deliveryPricingModelSchema.default("FLAT"),
    deliveryFeeMinor: z.number().int().nonnegative().default(150_000),
    deliveryBaseFeeMinor: z.number().int().nonnegative().default(0),
    deliveryPricePerKmMinor: z.number().int().nonnegative().default(0),
    deliveryLocationFees: z.array(deliveryLocationFeeSchema).default([]),
    menuCategories: z.array(menuCategorySchema).default([]),
    menuItems: z.array(menuItemSchema).default([]),
    itemModifierGroups: z.array(itemModifierGroupSchema).default([]),
    itemModifiers: z.array(itemModifierSchema).default([]),
    menuItemModifierGroups: z.array(menuItemModifierGroupSchema).default([]),
  })
  .passthrough()
  .transform((outlet) => {
    return {
      ...outlet,
      settlementSubaccountCode: outlet.settlementSubaccountCode ?? null,
    };
  });

export const rateOutletInputSchema = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string().max(1000).optional(),
});

export const masterOrderStatusSchema = z.enum([
  "PENDING_PAYMENT",
  "CONFIRMED",
  "PREPARING",
  "PARTIALLY_READY",
  "PARTIALLY_FULFILLED",
  "READY",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
]);

export const subOrderStatusSchema = z.enum([
  "PENDING",
  "ACCEPTED",
  "PREPARING",
  "READY",
  "COLLECTED",
  "DISPATCHED",
  "REJECTED",
]);

export const deliveryModeSchema = z.enum(["DELIVERY", "TAKEOUT"]);

export const refundRequestListQuerySchema = z
  .object({
    status: z.enum(["PENDING", "SUCCESS", "FAILED"]).optional(),
    reference: z.string().trim().min(2).max(120).optional(),
    customerId: z.uuid().optional(),
    requestedBy: z.uuid().optional(),
    dateFrom: z.iso.datetime().optional(),
    dateTo: z.iso.datetime().optional(),
    limit: z.int().min(1).max(100).optional(),
    offset: z.int().min(0).optional(),
  })
  .strict();

export const refundRequestItemSchema = z.object({
  refund: paymentRefundSchema,
  payment: z
    .object({
      id: z.uuid(),
      masterOrderId: z.uuid(),
      amountMinor: z.coerce.number().int().nonnegative(),
      currency: currencySchema,
      gateway: z.string().min(1),
      reference: z.string().min(1),
      status: z.enum(["PENDING", "SUCCESS", "FAILED"]),
      createdAt: z.iso.datetime(),
    })
    .nullable(),
  order: z
    .object({
      id: z.uuid(),
      customerId: z.uuid(),
      status: masterOrderStatusSchema,
      totalMinor: z.coerce.number().int().nonnegative(),
      currency: currencySchema,
      createdAt: z.iso.datetime(),
    })
    .nullable(),
  customer: z
    .object({
      id: z.uuid(),
      name: z.string().min(1),
    })
    .nullable(),
  requestedBy: z
    .object({
      id: z.uuid(),
      name: z.string().min(1),
      role: userRoleSchema,
    })
    .nullable(),
});

export const refundRequestListSchema = z.object({
  refundRequests: z.array(refundRequestItemSchema),
  total: z.int().nonnegative(),
  limit: z.int().min(1).max(100),
  offset: z.int().min(0),
  next: z.int().min(0).nullable(),
  previous: z.int().min(0).nullable(),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export const adminOrdersQuerySchema = z
  .object({
    outletId: z.uuid().optional(),
    status: masterOrderStatusSchema.optional(),
    subOrderStatus: subOrderStatusSchema.optional(),
    deliveryMode: deliveryModeSchema.optional(),
    customerId: z.uuid().optional(),
    dateFrom: z.iso.datetime().optional(),
    dateTo: z.iso.datetime().optional(),
    limit: z.int().min(1).max(100).optional(),
    offset: z.int().min(0).optional(),
  })
  .strict();

export const adminOrderMasterSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  riderId: z.uuid().nullable(),
  status: masterOrderStatusSchema,
  subtotalMinor: z.int().nonnegative(),
  deliveryFeeMinor: z.int().nonnegative(),
  serviceFeeMinor: z.int().nonnegative(),
  vatMinor: z.int().nonnegative(),
  discountMinor: z.int().nonnegative(),
  totalMinor: z.int().nonnegative(),
  currency: currencySchema,
  deliveryMode: deliveryModeSchema,
  deliveryAddress: z.string().nullable(),
  deliveryLatitude: z.number().nullable(),
  deliveryLongitude: z.number().nullable(),
  recipientPhone: z.string().nullable().optional(),
  paymentReference: z.string().nullable(),
  deliveryCode: z.string().nullable(),
  preparationTime: z.number().int().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
});

export const adminOrderSubOrderSchema = z.object({
  id: z.uuid(),
  masterOrderId: z.uuid(),
  outletId: z.uuid(),
  status: subOrderStatusSchema,
  pickupCode: z.string().nullable().optional(),
  subtotalMinor: z.int().nonnegative(),
  commissionMinor: z.int().nonnegative(),
  netMinor: z.int().nonnegative(),
  currency: currencySchema,
  preparationTime: z.number().int().nullable().optional(),
  preparationNote: z.string().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  preparationTimeMinutes: z.int().nonnegative().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
});

export const adminOrderLineItemSchema = z
  .object({
    id: z.uuid(),
    masterOrderId: z.uuid(),
    subOrderId: z.uuid(),
    outletId: z.uuid(),
    menuItemId: z.uuid().nullable(),
    itemNameSnapshot: z.string().min(1),
    baseUnitPriceMinor: z.coerce.number().int().nonnegative().nullish(),
    unitPriceMinor: z.int().nonnegative(),
    quantity: z.int().positive(),
    lineTotalMinor: z.int().nonnegative(),
    currency: currencySchema,
    customerNote: z.string().nullable().optional(),
    modifiersSnapshot: z
      .array(
        z
          .object({
            id: z.uuid().optional(),
            name: z.string().min(1),
            priceDeltaMinor: z.coerce.number().int().default(0),
          })
          .passthrough(),
      )
      .nullish()
      .transform((value) => value ?? []),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullable(),
  })
  .transform((item) => {
    const modifierTotal = item.modifiersSnapshot.reduce(
      (sum, mod) => sum + (mod.priceDeltaMinor || 0),
      0,
    );
    return {
      ...item,
      baseUnitPriceMinor:
        item.baseUnitPriceMinor ?? Math.max(0, item.unitPriceMinor - modifierTotal),
    };
  });

export const adminOrderSummarySchema = z.object({
  order: adminOrderMasterSchema,
  subOrders: z.array(adminOrderSubOrderSchema),
  lineItems: z.array(adminOrderLineItemSchema),
});

export const adminOrdersResultSchema = z.object({
  orders: z.array(adminOrderSummarySchema),
  total: z.int().nonnegative(),
  totalSubOrders: z.int().nonnegative(),
  limit: z.int().min(1).max(100),
  offset: z.int().min(0),
  next: z.int().min(0).nullable(),
  previous: z.int().min(0).nullable(),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export const orderSummarySchema = z.object({
  id: z.uuid(),
  status: masterOrderStatusSchema,
  deliveryAddress: z.string().nullable(),
  subOrders: z.array(
    z.object({
      outletName: z.string(),
    }),
  ),
  totalAmountMinor: z.int().nonnegative(),
  createdAt: z.iso.datetime(),
});

export const customerOrderSchema = z
  .object({
    id: z.string().min(1),
    customerId: z.uuid(),
    riderId: z.uuid().nullable(),
    status: z.string().trim().min(1),
    customerViewId: z.string().min(1).optional(),
    sourceMasterOrderId: z.uuid().optional(),
    splitKind: z.enum(["FULFILLED", "FAILED"]).optional(),
    refundSubOrderIds: z.array(z.uuid()).optional(),
    refundableMinor: z.coerce.number().int().nonnegative().optional(),
    subtotalMinor: z.coerce.number().int().nonnegative(),
    deliveryFeeMinor: z.coerce.number().int().nonnegative().default(0),
    serviceFeeMinor: z.coerce.number().int().nonnegative().default(0),
    vatMinor: z.coerce.number().int().nonnegative().default(0),
    discountMinor: z.coerce.number().int().nonnegative().default(0),
    totalMinor: z.coerce.number().int().nonnegative(),
    currency: currencySchema.default("NGN"),
    deliveryMode: z.enum(["DELIVERY", "TAKEOUT"]),
    deliveryAddress: z.string().nullable(),
    deliveryLatitude: z.coerce.number().min(-90).max(90).nullable(),
    deliveryLongitude: z.coerce.number().min(-180).max(180).nullable(),
    recipientPhone: z.string().nullable().optional(),
    paymentReference: z.string().nullable(),
    deliveryCode: z.string().nullable(),
    preparationTime: z.coerce.number().int().nullable().optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    deletedAt: z.iso.datetime().nullable(),
  })
  .passthrough();

export const paginatedCustomerOrdersSchema = z.object({
  orders: z.array(customerOrderSchema),
  total: z.int().nonnegative(),
  limit: z.int().min(1).max(100),
  offset: z.int().min(0),
  next: z.int().min(0).nullable(),
  previous: z.int().min(0).nullable(),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export const riderLocationSchema = z
  .object({
    riderId: z.uuid().nullable(),
    masterOrderId: z.uuid().nullable(),
    latitude: z.coerce.number().min(-90).max(90),
    longitude: z.coerce.number().min(-180).max(180),
    recordedAt: z.string().min(1),
  })
  .passthrough();

export const orderStatusEventSchema = z.object({
  id: z.uuid(),
  masterOrderId: z.uuid(),
  subOrderId: z.uuid().nullable(),
  masterStatus: z.string().nullable(),
  subOrderStatus: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const subOrderDetailSchema = z
  .object({
    id: z.uuid(),
    masterOrderId: z.uuid(),
    outletId: z.uuid(),
    status: z.string().min(1),
    subtotalMinor: z.coerce.number().int().nonnegative(),
    commissionMinor: z.coerce.number().int().nonnegative().default(0),
    netMinor: z.coerce.number().int().nonnegative().default(0),
    currency: currencySchema.default("NGN"),
    preparationTime: z.coerce.number().int().nullable().optional(),
    preparationNote: z.string().nullable().optional(),
    rejectionReason: z.string().nullable().optional(),
    createdAt: z.string().min(1),
  })
  .passthrough();

export const orderLineItemSchema = z
  .object({
    id: z.uuid(),
    masterOrderId: z.uuid(),
    subOrderId: z.uuid(),
    outletId: z.uuid(),
    menuItemId: z.uuid().nullable(),
    itemNameSnapshot: z.string().min(1),
    baseUnitPriceMinor: z.coerce.number().int().nonnegative().nullish(),
    unitPriceMinor: z.coerce.number().int(),
    quantity: z.coerce.number().int().positive(),
    lineTotalMinor: z.coerce.number().int(),
    currency: currencySchema.default("NGN"),
    modifiersSnapshot: z
      .array(
        z
          .object({
            id: z.uuid().optional(),
            name: z.string().min(1),
            priceDeltaMinor: z.coerce.number().int().default(0),
          })
          .passthrough(),
      )
      .nullish()
      .transform((value) => value ?? []),
    customerNote: z.string().nullable().optional(),
  })
  .passthrough()
  .transform((item) => {
    const modifierTotal = item.modifiersSnapshot.reduce(
      (sum, mod) => sum + (mod.priceDeltaMinor || 0),
      0,
    );
    return {
      ...item,
      baseUnitPriceMinor:
        item.baseUnitPriceMinor ?? Math.max(0, item.unitPriceMinor - modifierTotal),
    };
  });

export const riderInfoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  vehicleType: z.string().nullable().optional(),
  plateNumber: z.string().nullable().optional(),
});

export const orderDetailSchema = z
  .object({
    order: customerOrderSchema,
    subOrders: z
      .array(subOrderDetailSchema)
      .nullish()
      .transform((value) => value ?? []),
    lineItems: z
      .array(orderLineItemSchema)
      .nullish()
      .transform((value) => value ?? []),
    events: z
      .array(orderStatusEventSchema)
      .nullish()
      .transform((value) => value ?? []),
    latestRiderLocation: riderLocationSchema.nullish().transform((value) => value ?? null),
    rider: riderInfoSchema.nullish().transform((value) => value ?? null),
  })
  .passthrough();

export const riderDispatchSchema = z.object({
  orderId: z.uuid(),
  status: masterOrderStatusSchema,
  deliveryCodeRequired: z.literal(true),
  deliveryAddress: z.string().nullable(),
  deliveryLatitude: z.number().nullable(),
  deliveryLongitude: z.number().nullable(),
  customerId: z.uuid(),
  riderId: z.uuid().nullable(),
  outlets: z.array(
    z.object({
      subOrderId: z.uuid(),
      outletId: z.uuid(),
      outletName: z.string().min(1),
      pickupAddress: z.string().nullable(),
      pickupLatitude: z.number().nullable(),
      pickupLongitude: z.number().nullable(),
      pickupCode: z.string().min(1),
      status: subOrderStatusSchema,
      preparationNote: z.string().nullable().optional(),
      rejectionReason: z.string().nullable().optional(),
      items: z.array(
        z.object({
          id: z.uuid(),
          name: z.string().min(1),
          quantity: z.coerce.number().int().positive(),
          baseUnitPriceMinor: z.coerce.number().int().nonnegative().optional(),
          unitPriceMinor: z.coerce.number().int().nonnegative().optional(),
          lineTotalMinor: z.coerce.number().int().nonnegative().optional(),
          modifiers: z.array(z.unknown()),
        }),
      ),
    }),
  ),
});

export const rejectAssignedOrderInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export const rejectAssignedOrderResultSchema = z.object({
  rejected: z.literal(true),
  reassigned: z.boolean(),
  previousRiderId: z.uuid(),
  riderId: z.uuid().nullable(),
  order: orderDetailSchema,
});

export const uploadedImageSchema = z.object({
  url: z.url(),
  publicId: z.string().min(1),
});

export const notificationSchema = z.object({
  id: z.uuid(),
  recipientId: z.uuid(),
  recipientRole: z.string().trim().min(1),
  type: z.string().trim().min(1),
  title: z.string().trim().min(1),
  body: z.string().trim().min(1),
  data: z.record(z.string(), z.unknown()).default({}),
  isRead: z.boolean(),
  createdAt: z.iso.datetime(),
});

export const promoSchema = z.object({
  id: z.uuid(),
  code: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  discountTarget: z.enum(["DELIVERY", "ORDER"]),
  discountPercent: z.int().min(1).max(100),
  scope: z.enum(["ALL_OUTLETS", "OUTLET"]),
  outletId: z.uuid().nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  isActive: z.boolean(),
  deepLink: z.string().nullable(),
  imageUrl: z.string().nullable().default(null),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const createPromoInputSchema = z
  .object({
    type: z.string().trim().min(2).max(80).default("PROMO"),
    title: z.string().trim().min(2).max(160),
    body: z.string().trim().min(2).max(2_000),
    recipientRole: z.enum(["CUSTOMER", "ADMIN", "RIDER"]).default("CUSTOMER"),
    promoCode: z.string().trim().min(2).max(80),
    discountTarget: z.enum(["DELIVERY", "ORDER"]),
    discountPercent: z.int().min(1).max(100),
    scope: z.enum(["ALL_OUTLETS", "OUTLET"]),
    outletId: z.uuid().optional(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    deepLink: z.string().trim().max(512).optional(),
    imageUrl: z.string().trim().max(512).nullable().optional(),
  })
  .strict();

export const updatePromoInputSchema = createPromoInputSchema
  .omit({ type: true, recipientRole: true, promoCode: true })
  .partial()
  .extend({ isActive: z.boolean().optional(), outletId: z.uuid().nullable().optional() })
  .strict();

export const togglePromoActiveInputSchema = z.object({ isActive: z.boolean() }).strict();

export const notificationPreferencesSchema = z.object({
  promotions: z.boolean(),
  discounts: z.boolean(),
  seasonalOffers: z.boolean(),
  orderStatus: z.literal(true),
});

export const updateNotificationPreferencesInputSchema = z
  .object({
    promotions: z.boolean().optional(),
    discounts: z.boolean().optional(),
    seasonalOffers: z.boolean().optional(),
    orderStatus: z.boolean().optional(),
  })
  .strict();

export const notificationCampaignTargetSegmentSchema = z.enum([
  "ALL_CUSTOMERS",
  "ACTIVE_CUSTOMERS",
  "CUSTOMERS_WITH_DEVICE_TOKEN",
]);

export const notificationCampaignStatusSchema = z.enum([
  "SCHEDULED",
  "DISPATCHING",
  "SENT",
  "FAILED",
]);

export const createNotificationCampaignInputSchema = z.object({
  title: z.string().trim().min(2).max(160),
  body: z.string().trim().min(2).max(2_000),
  targetSegment: notificationCampaignTargetSegmentSchema,
  scheduledAt: z.iso.datetime(),
  deepLink: z.string().trim().max(512).optional(),
});

export const notificationCampaignSchema = z.object({
  id: z.uuid(),
  createdById: z.uuid(),
  title: z.string(),
  body: z.string(),
  targetSegment: notificationCampaignTargetSegmentSchema,
  deepLink: z.string().nullable(),
  scheduledAt: z.iso.datetime(),
  status: notificationCampaignStatusSchema,
  totalTargeted: z.int().nonnegative(),
  sentCount: z.int().nonnegative(),
  failedCount: z.int().nonnegative(),
  dispatchedAt: z.iso.datetime().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const operationsStatsQuerySchema = z
  .object({
    outletId: z.uuid().optional(),
  })
  .strict();

export const orderPulseRangeSchema = z.enum(["TODAY", "LAST_7_DAYS", "LAST_30_DAYS"]);

export const orderPulseQuerySchema = operationsStatsQuerySchema.extend({
  range: orderPulseRangeSchema.optional(),
});

export const operationsSummarySchema = z.object({
  activeOutlets: z.int().nonnegative(),
  openMasterOrders: z.int().nonnegative(),
  delayedSubOrders: z.int().nonnegative(),
  pendingSettlements: z.int().nonnegative(),
});

export const orderPulsePointSchema = z.object({
  bucketStart: z.iso.datetime(),
  label: z.string().min(1),
  orderCount: z.int().nonnegative(),
});

export const orderPulseSchema = z.object({
  range: orderPulseRangeSchema,
  outletId: z.uuid().nullable(),
  points: z.array(orderPulsePointSchema),
});

export const operationsQueueItemSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("DELAYED_KITCHEN_TICKETS"),
    count: z.int().nonnegative(),
    oldestDelayMinutes: z.int().nonnegative().nullable(),
  }),
  z.object({
    type: z.literal("PAUSED_OUTLETS"),
    count: z.int().nonnegative(),
  }),
]);

export const operationsQueueSchema = z.object({
  outletId: z.uuid().nullable(),
  delayedKitchenTickets: z.int().nonnegative(),
  oldestDelayMinutes: z.int().nonnegative().nullable(),
  pausedOutlets: z.int().nonnegative(),
  items: z.array(operationsQueueItemSchema),
});

export type Money = z.infer<typeof moneySchema>;
export type ApiResponse<T> = {
  data: T;
  message: string;
  status: number;
};
export type ApiErrorData = z.infer<typeof apiErrorDataSchema>;
export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>;
export type CustomerStatus = z.infer<typeof customerStatusSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;
export type ResetPasswordResult = z.infer<typeof resetPasswordResultSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInputSchema>;
export type ForgotPasswordResult = z.infer<typeof forgotPasswordResultSchema>;
export type ResendVerificationInput = z.infer<typeof resendVerificationInputSchema>;
export type ResendVerificationResult = z.infer<typeof resendVerificationResultSchema>;
export type UserRole = z.infer<typeof userRoleSchema>;
export type RegisterCustomerInput = z.infer<typeof registerCustomerInputSchema>;
export type RegistrationResult = z.infer<typeof registrationResultSchema>;
export type VerificationChannel = z.infer<typeof verificationChannelSchema>;
export type VerifyUserInput = z.infer<typeof verifyUserInputSchema>;
export type UserVerificationResult = z.infer<typeof userVerificationResultSchema>;
export type LoginInput = z.infer<typeof loginInputSchema>;
export type LoginResult = z.infer<typeof loginResultSchema>;
export type RefreshSessionResult = z.infer<typeof refreshSessionResultSchema>;
export type LogoutResult = z.infer<typeof logoutResultSchema>;
export type CreateAdminInput = z.infer<typeof createAdminInputSchema>;
export type AdminResult = z.infer<typeof adminResultSchema>;
export type AuditLog = z.infer<typeof auditLogSchema>;
export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>;
export type PaginatedAuditLogs = z.infer<typeof paginatedAuditLogsSchema>;
export type DatabaseBackupStatus = z.infer<typeof databaseBackupStatusSchema>;
export type DatabaseBackupSettings = z.infer<typeof databaseBackupSettingsSchema>;
export type UpdateDatabaseBackupSettingsInput = z.infer<
  typeof updateDatabaseBackupSettingsInputSchema
>;
export type DatabaseBackupRunResult = z.infer<typeof databaseBackupRunResultSchema>;
export type CreateRiderInput = z.infer<typeof createRiderInputSchema>;
export type RiderResult = z.infer<typeof riderResultSchema>;
export type RiderAdmin = z.infer<typeof riderAdminSchema>;
export type UpdateRiderInput = z.infer<typeof updateRiderInputSchema>;

export type OutletAdmin = z.infer<typeof outletAdminSchema>;
export type PlatformUser = z.infer<typeof platformUserSchema>;
export type PlatformUserList = z.infer<typeof platformUserListSchema>;
export type PlatformUserListQuery = z.infer<typeof platformUserListQuerySchema>;
export type ResendVerificationCodeInput = z.infer<typeof resendVerificationCodeInputSchema>;
export type ResendVerificationCodeResult = z.infer<typeof resendVerificationCodeResultSchema>;
export type OutletSummary = z.infer<typeof outletSummarySchema>;
export type ItemModifierGroup = z.infer<typeof itemModifierGroupSchema>;
export type ItemModifier = z.infer<typeof itemModifierSchema>;
export type MenuItemModifierGroup = z.infer<typeof menuItemModifierGroupSchema>;
export type MenuItem = z.infer<typeof menuItemSchema>;
export type MenuItemsPage = z.infer<typeof menuItemsPageSchema>;
export type UpdateMenuItemAvailabilityInput = z.infer<typeof updateMenuItemAvailabilityInputSchema>;
export type RateMenuItemInput = z.infer<typeof rateMenuItemInputSchema>;
export type MasterOrderStatus = z.infer<typeof masterOrderStatusSchema>;
export type SubOrderStatus = z.infer<typeof subOrderStatusSchema>;
export type DeliveryMode = z.infer<typeof deliveryModeSchema>;
export type AdminOrdersQuery = z.infer<typeof adminOrdersQuerySchema>;
export type AdminOrderMaster = z.infer<typeof adminOrderMasterSchema>;
export type AdminOrderSubOrder = z.infer<typeof adminOrderSubOrderSchema>;
export type AdminOrderLineItem = z.infer<typeof adminOrderLineItemSchema>;
export type AdminOrderSummary = z.infer<typeof adminOrderSummarySchema>;
export type AdminOrdersResult = z.infer<typeof adminOrdersResultSchema>;
export type OperationsStatsQuery = z.infer<typeof operationsStatsQuerySchema>;
export type OrderPulseRange = z.infer<typeof orderPulseRangeSchema>;
export type OrderPulseQuery = z.infer<typeof orderPulseQuerySchema>;
export type OperationsSummary = z.infer<typeof operationsSummarySchema>;
export type OrderPulsePoint = z.infer<typeof orderPulsePointSchema>;
export type OrderPulse = z.infer<typeof orderPulseSchema>;
export type OperationsQueueItem = z.infer<typeof operationsQueueItemSchema>;
export type OperationsQueue = z.infer<typeof operationsQueueSchema>;
export type UserProfile = z.infer<typeof userProfileSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;
export type ProfileUpdateResult = z.infer<typeof profileUpdateResultSchema>;
export type VerifyProfileChangeInput = z.infer<typeof verifyProfileChangeInputSchema>;
export type CreateDeliveryAddressInput = z.infer<typeof createDeliveryAddressInputSchema>;
export type DeliveryAddressSummary = z.infer<typeof deliveryAddressSummarySchema>;
export type ValidateAddressInput = z.infer<typeof validateAddressInputSchema>;
export type ValidateAddressResult = z.infer<typeof validateAddressResultSchema>;
export type DeliveryAddressProvider = z.infer<typeof deliveryAddressProviderSchema>;
export type DeliveryAddressSuggestion = z.infer<typeof deliveryAddressSuggestionSchema>;
export type ResolveDeliveryAddressInput = z.infer<typeof resolveDeliveryAddressInputSchema>;
export type ResolvedDeliveryAddress = z.infer<typeof resolvedDeliveryAddressSchema>;
export type GeofenceZone = z.infer<typeof geofenceZoneSchema>;
export type CreateGeofenceZoneInput = z.infer<typeof createGeofenceZoneInputSchema>;
export type UpdateGeofenceZoneInput = z.infer<typeof updateGeofenceZoneInputSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;
export type ChangePasswordResult = z.infer<typeof changePasswordResultSchema>;
export type InitiatePaymentInput = z.infer<typeof initiatePaymentInputSchema>;
export type RetryPaymentInput = z.infer<typeof retryPaymentInputSchema>;
export type InitiatePaymentResult = z.infer<typeof initiatePaymentResultSchema>;
export type PaymentVerifyResult = z.infer<typeof paymentVerifyResultSchema>;
export type ProcessRefundInput = z.infer<typeof processRefundInputSchema>;
export type RequestRefundInput = z.infer<typeof requestRefundInputSchema>;
export type PaymentRefund = z.infer<typeof paymentRefundSchema>;
export type RefundRequestListQuery = z.infer<typeof refundRequestListQuerySchema>;
export type RefundRequestItem = z.infer<typeof refundRequestItemSchema>;
export type RefundRequestList = z.infer<typeof refundRequestListSchema>;
export type OrderSummary = z.infer<typeof orderSummarySchema>;
export type CustomerOrder = z.infer<typeof customerOrderSchema>;
export type PaginatedCustomerOrders = z.infer<typeof paginatedCustomerOrdersSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type Promo = z.infer<typeof promoSchema>;
export type CreatePromoInput = z.infer<typeof createPromoInputSchema>;
export type UpdatePromoInput = z.infer<typeof updatePromoInputSchema>;
export type TogglePromoActiveInput = z.infer<typeof togglePromoActiveInputSchema>;
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;
export type UpdateNotificationPreferencesInput = z.infer<
  typeof updateNotificationPreferencesInputSchema
>;
export type NotificationCampaignTargetSegment = z.infer<
  typeof notificationCampaignTargetSegmentSchema
>;
export type NotificationCampaignStatus = z.infer<typeof notificationCampaignStatusSchema>;
export type CreateNotificationCampaignInput = z.infer<typeof createNotificationCampaignInputSchema>;
export type NotificationCampaign = z.infer<typeof notificationCampaignSchema>;
export type MenuCategorySummary = z.infer<typeof menuCategorySchema>;
export type MenuItemSummary = z.infer<typeof menuItemSchema>;
export type ItemModifierSummary = z.infer<typeof itemModifierSchema>;
export type ItemModifierGroupSummary = z.infer<typeof itemModifierGroupSchema>;
export type RiderLocation = z.infer<typeof riderLocationSchema>;
export type RiderInfo = z.infer<typeof riderInfoSchema>;
export type OrderStatusEvent = z.infer<typeof orderStatusEventSchema>;
export type SubOrderDetail = z.infer<typeof subOrderDetailSchema>;
export type OrderLineItem = z.infer<typeof orderLineItemSchema>;
export type OrderDetail = z.infer<typeof orderDetailSchema>;
export type RiderDispatch = z.infer<typeof riderDispatchSchema>;
export type RejectAssignedOrderInput = z.infer<typeof rejectAssignedOrderInputSchema>;
export type RejectAssignedOrderResult = z.infer<typeof rejectAssignedOrderResultSchema>;
export type UploadedImage = z.infer<typeof uploadedImageSchema>;

export const paginatedMenuItemsSchema = menuItemsPageSchema;

export type PaginatedMenuItems = z.infer<typeof paginatedMenuItemsSchema>;

export const platformChargesSchema = z.object({
  platformCommissionBps: z.int().min(0).max(10_000),
  defaultVatBps: z.int().min(0).max(10_000),
  deliveryFeeMinor: z.int().nonnegative(),
  serviceFeeMinor: z.int().nonnegative(),
  currency: currencySchema,
});

export const updatePlatformChargesInputSchema = platformChargesSchema
  .omit({ currency: true })
  .partial()
  .strict();

export const pickupSubOrderInputSchema = z
  .object({
    note: z.string().max(500).optional(),
  })
  .strict();

export type PlatformCharges = z.infer<typeof platformChargesSchema>;
export type UpdatePlatformChargesInput = z.infer<typeof updatePlatformChargesInputSchema>;
export type PickupSubOrderInput = z.infer<typeof pickupSubOrderInputSchema>;
export type RateOutletInput = z.infer<typeof rateOutletInputSchema>;
export type SettlementStatus = z.infer<typeof settlementStatusSchema>;
export type OutletSettlementSummary = z.infer<typeof outletSettlementSummarySchema>;
export type OutletSettlementExport = z.infer<typeof outletSettlementExportSchema>;

export const preparationSuggestionSchema = z.object({
  id: z.uuid(),
  text: z.string().min(1).max(255),
  outletId: z.uuid().nullable(),
  menuItemId: z.uuid().nullable(),
  isActive: z.boolean(),
  sortOrder: z.int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const createPreparationSuggestionInputSchema = z
  .object({
    text: z.string().trim().min(1).max(255),
    outletId: z.uuid().nullable().optional(),
    menuItemId: z.uuid().nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.int().nonnegative().optional(),
  })
  .strict();

export const updatePreparationSuggestionInputSchema = createPreparationSuggestionInputSchema
  .partial()
  .strict();

export const queryPreparationSuggestionsInputSchema = z
  .object({
    outletId: z.uuid().optional(),
    menuItemId: z.uuid().optional(),
    q: z.string().optional(),
  })
  .strict();

export type PreparationSuggestion = z.infer<typeof preparationSuggestionSchema>;
export type CreatePreparationSuggestionInput = z.infer<
  typeof createPreparationSuggestionInputSchema
>;
export type UpdatePreparationSuggestionInput = z.infer<
  typeof updatePreparationSuggestionInputSchema
>;
export type QueryPreparationSuggestionsInput = z.infer<
  typeof queryPreparationSuggestionsInputSchema
>;
